import {
  Mountain,
  Clapperboard,
  User,
  Parentheses,
  MessageCircle,
  ArrowRight,
} from "lucide-react";
import React from "react";

export type BlockType =
  | "SCENE_HEADING"
  | "ACTION"
  | "CHARACTER"
  | "PARENTHETICAL"
  | "DIALOGUE"
  | "TRANSITION";

export type TimeOfDay = "DAY" | "NIGHT";
export const timeOfDayOptions: TimeOfDay[] = ["DAY", "NIGHT"];

export interface Block {
  id: string;
  type: BlockType;
  text: string;
  position?: string; // Lexicographical ordering key
  sceneNumber?: string;
  sceneType?: "INT." | "EXT." | "INT/EXT.";
  timeOfDay?: TimeOfDay;
}

export const blockTypes: BlockType[] = [
  "SCENE_HEADING",
  "ACTION",
  "CHARACTER",
  "PARENTHETICAL",
  "DIALOGUE",
  "TRANSITION",
];

export const uuid = (): string => {
  // Time prefix for chronological sorting
  const timestamp = Date.now().toString(36);

  // 80 bits cryptographic randomness (more than enough)
  const bytes = new Uint8Array(10);
  crypto.getRandomValues(bytes);

  const random = Array.from(bytes, (b) => b.toString(36).padStart(2, "0")).join(
    "",
  );

  return `${timestamp}${random}`;
};

export const icons: Record<BlockType, React.ReactNode> = {
  SCENE_HEADING: <Mountain className="w-[18px] h-[18px]" strokeWidth={1.5} />,
  ACTION: <Clapperboard className="w-[18px] h-[18px]" strokeWidth={1.5} />,
  CHARACTER: <User className="w-[18px] h-[18px]" strokeWidth={1.5} />,
  PARENTHETICAL: (
    <Parentheses className="w-[18px] h-[18px]" strokeWidth={1.5} />
  ),
  DIALOGUE: <MessageCircle className="w-[18px] h-[18px]" strokeWidth={1.5} />,
  TRANSITION: <ArrowRight className="w-[18px] h-[18px]" strokeWidth={1.5} />,
};

export const blockStyles: Record<
  BlockType,
  { label: string; className: string; inputStyle: React.CSSProperties }
> = {
  SCENE_HEADING: {
    label: "Scene Heading",
    className: "uppercase font-bold text-zinc-900 flex items-center gap-3",
    inputStyle: {
      textTransform: "uppercase",
      fontWeight: 700,
      maxWidth: "6.0in",
      outline: "none",
      whiteSpace: "pre-wrap",
      overflowWrap: "break-word",
      wordBreak: "break-word",
    },
  },
  ACTION: {
    label: "Action",
    className: "text-zinc-800",
    inputStyle: {
      maxWidth: "6.0in",
      outline: "none",
      whiteSpace: "pre-wrap",
      overflowWrap: "break-word",
      wordBreak: "break-word",
      lineHeight: "15pt",
    },
  },
  CHARACTER: {
    label: "Character",
    className: "uppercase text-zinc-900 tracking-widest",
    inputStyle: {
      textTransform: "uppercase",
      textAlign: "left",
      marginLeft: "2.0in",
      maxWidth: "4.0in",
      letterSpacing: "0.1em",
      outline: "none",
      whiteSpace: "pre-wrap",
      overflowWrap: "break-word",
      wordBreak: "break-word",
    },
  },
  PARENTHETICAL: {
    label: "Parenthetical",
    className: "text-zinc-600",
    inputStyle: {
      fontStyle: "normal",
      maxWidth: "3.0in",
      marginLeft: "1.5in",
      textTransform: "lowercase",
      outline: "none",
      whiteSpace: "pre-wrap",
      overflowWrap: "break-word",
      wordBreak: "break-word",
      textAlign: "left",
    },
  },
  DIALOGUE: {
    label: "Dialogue",
    className: "text-zinc-900",
    inputStyle: {
      marginLeft: "1.0in",
      maxWidth: "3.5in",
      outline: "none",
      whiteSpace: "pre-wrap",
      overflowWrap: "break-word",
      wordBreak: "break-word",
      fontSize: "1.05rem",
      textAlign: "left",
      lineHeight: "12pt",
    },
  },
  TRANSITION: {
    label: "Transition",
    className: "uppercase font-bold text-right text-zinc-900",
    inputStyle: {
      textTransform: "uppercase",
      fontWeight: 600,
      textAlign: "right",
      outline: "none",
      whiteSpace: "pre-wrap",
      overflowWrap: "break-word",
      wordBreak: "break-word",
    },
  },
};
